import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import { useState, useCallback, useEffect } from 'react';
import { useGetAndSet } from './useGetAndSet.js';
import { useGetSync } from './useGetSync.js';

/**
 * Custom hook for iterating through an array and returning the current element and a function
 * to select the next element.
 * @template T
 * @param {T[]} array - The array to iterate through
 * @return {[current: T, next: () => boolean]} An array with the current element in the first
 * position and a function to select the next element in the second. The function will return
 * true if a new element is being selected and false if there are no more elements.
 */
function useArrayIterator(array) {
  var _useState = useState(),
    _useState2 = _slicedToArray(_useState, 2),
    current = _useState2[0],
    setCurrent = _useState2[1];
  var _useGetAndSet = useGetAndSet(0),
    _useGetAndSet2 = _slicedToArray(_useGetAndSet, 2),
    getCurrentIndex = _useGetAndSet2[0],
    setCurrentIndex = _useGetAndSet2[1];
  var getArray = useGetSync(array);
  var isThereMoreElements = useCallback(function () {
    var _getArray;
    return ((_getArray = getArray()) === null || _getArray === void 0 ? void 0 : _getArray.length) > getCurrentIndex() + 1;
  }, [getCurrentIndex, getArray]);
  var next = useCallback(function () {
    var _getArray2;
    if (!isThereMoreElements()) return false;
    var sourceIndex = getCurrentIndex();
    setCurrentIndex(sourceIndex + 1);
    setCurrent((_getArray2 = getArray()) === null || _getArray2 === void 0 ? void 0 : _getArray2[sourceIndex + 1]);
    return true;
  }, [getCurrentIndex, isThereMoreElements, setCurrentIndex, getArray]);
  useEffect(function () {
    setCurrentIndex(0);
    setCurrent(array === null || array === void 0 ? void 0 : array[0]);
  }, [setCurrentIndex, array]);
  return [current, next];
}

export { useArrayIterator };
//# sourceMappingURL=useArrayIterator.js.map
